import api from './api';
import { Label } from '../types/label';

export interface LabelImportResult {
  message: string;
  imported: number;
  updated: number;
  duplicates: number;
  errors: number;
  details: {
    duplicateLabels: string[];
    errorMessages: string[];
  };
}

export interface LabelStats {
  total: number;
  withIcons: number;
  withoutIcons: number;
}

class LabelService {
  // Alle Labels abrufen
  async getAllLabels(): Promise<Label[]> {
    try {
      const response = await api.get('/labels');
      return response.data;
    } catch (error) {
      console.error('Error fetching labels:', error);
      throw new Error('Fehler beim Laden der Labels');
    }
  }

  // Label nach ID abrufen
  async getLabelById(id: string): Promise<Label> {
    try {
      const response = await api.get(`/labels/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching label:', error);
      throw new Error('Fehler beim Laden des Labels');
    }
  }

  // Neues Label erstellen
  async createLabel(labelData: Partial<Label>): Promise<Label> {
    try {
      const response = await api.post('/labels', labelData);
      return response.data;
    } catch (error) {
      console.error('Error creating label:', error);
      throw new Error('Fehler beim Erstellen des Labels');
    }
  }

  // Label aktualisieren
  async updateLabel(id: string, labelData: Partial<Label>): Promise<Label> {
    try {
      const response = await api.put(`/labels/${id}`, labelData);
      return response.data;
    } catch (error) {
      console.error('Error updating label:', error);
      throw new Error('Fehler beim Aktualisieren des Labels');
    }
  }

  // Label löschen
  async deleteLabel(id: string): Promise<void> {
    try {
      await api.delete(`/labels/${id}`);
    } catch (error) {
      console.error('Error deleting label:', error);
      throw new Error('Fehler beim Löschen des Labels');
    }
  }

  // Labels suchen
  async searchLabels(query: string): Promise<Label[]> {
    try {
      const response = await api.get('/labels/search', {
        params: { q: query }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching labels:', error);
      throw new Error('Fehler bei der Label-Suche');
    }
  }

  // Label-Statistiken abrufen
  async getStats(): Promise<LabelStats> {
    try {
      const response = await api.get('/labels/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching label stats:', error);
      throw new Error('Fehler beim Laden der Label-Statistiken');
    }
  }

  // Labels aus Excel importieren
  async importFromExcel(file: File): Promise<LabelImportResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/labels/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error importing labels:', error);
      throw new Error('Fehler beim Importieren der Labels');
    }
  }
}

export const labelService = new LabelService();
